/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, formatToDatePeriod, getMaxTickLimit, getLabels, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                margin: '0 -10px',
                flexFlow: 'row wrap'
            },
            '& .col': {
                flex: 1,
                margin: '0 10px 10px 10px',
                padding: 10,
                fontSize: 16,
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                '& .value': {
                    fontSize: '2.5rem',
                    padding: '20px 0',
                },
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
            textAlign: 'center'
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum LastCountEnum {
    PREV_MONTH,
    MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface FeesBilledInLastFinancialYearChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    last: LastCountEnum;
    months: string[];
    counts: number[];
    currentYearTotal: number;
    currentTotal: number;
    leftPeriodString: string;
    rightPeriodString: string;
    symbol: string;
}

export const FeesRecordedInFinancialYearChart: React.FC<FeesBilledInLastFinancialYearChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TimeFeesData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.MONTH,
        months: [],
        counts: [],
        currentYearTotal: 0,
        currentTotal: 0,
        leftPeriodString: formatToDatePeriod('monthToDate'),
        rightPeriodString: formatToDatePeriod('yearToDate'),
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let months: string[] = [];
                let counts: number[] = [];
                let currentTotal: number = 0;
                let currentYearTotal: number = 0;
                let leftPeriodString: string = '';
                let rightPeriodString: string = formatToDatePeriod('yearToDate');

                if (!error && data) {
                    var lastMonth = data.statistics.time.feesRecordedYearToDate.lastMonth;
                    var previousMonth = data.statistics.time.feesRecordedYearToDate.previousMonth;
                    var lastYear = data.statistics.time.feesRecordedYearToDate.lastFinancialYear;
                    var prevYear = data.statistics.time.feesRecordedYearToDate.previousFinancialYear;

                    currentYearTotal = lastYear.summary.lastPeriodTotal;

                    if (state.last === LastCountEnum.MONTH) {
                        months = lastMonth.timesheetGrouping.map((s) => s.group);
                        counts = lastMonth.timesheetGrouping.map((s) => s.value);
                        currentTotal = lastMonth.summary.lastPeriodTotal;
                        leftPeriodString = formatToDatePeriod('monthToDate');
                    } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                        months = lastYear.timesheetGrouping.map((s) => s.group);
                        counts = lastYear.timesheetGrouping.map((s) => s.value);
                        currentTotal = lastYear.summary.lastPeriodTotal;

                        leftPeriodString = formatToDatePeriod('yearToDate');
                    } else if (state.last === LastCountEnum.PREV_MONTH) {
                        months = previousMonth.timesheetGrouping.map((s) => s.group);
                        counts = previousMonth.timesheetGrouping.map((s) => s.value);
                        currentTotal = previousMonth.summary.lastPeriodTotal;

                        if (previousMonth.financialYearToPreviousMonthSummary) {
                            currentYearTotal = previousMonth.financialYearToPreviousMonthSummary.lastPeriodTotal;
                        } else {
                            currentYearTotal = 0;
                        }

                        leftPeriodString = formatToDatePeriod('previousMonth');
                        rightPeriodString = formatToDatePeriod('previousMonthYearToDate');
                    } else {
                        months = prevYear.timesheetGrouping.map((s) => s.group);
                        counts = prevYear.timesheetGrouping.map((s) => s.value);
                        currentTotal = prevYear.summary.lastPeriodTotal;

                        leftPeriodString = formatToDatePeriod('previousYearToDate');
                    }
                }

                return {
                    ...prevState,
                    months,
                    counts,
                    currentTotal,
                    currentYearTotal,
                    leftPeriodString,
                    rightPeriodString,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('IsTimesheetFeesInFinancialYearChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isFinancialYear: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (!isFinancialYear) {
                if (state.last === LastCountEnum.MONTH) {
                    searchCriterias.push({ property: 'MonthToDate', operator: '', comparison: 'curr' });
                } else if (state.last === LastCountEnum.PREV_MONTH) {
                    searchCriterias.push({ property: 'MonthToDate', operator: '', comparison: 'prev' });
                } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'curr' });
                } else if (state.last === LastCountEnum.PREV_FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'prev' });
                }
            } else {
                var period = 'curr';

                if (state.last === LastCountEnum.PREV_MONTH) {
                    period = 'prevMonth';
                }
                searchCriterias.push({ property: 'FinancialYear', operator: '', comparison: 'curr', period: period });
            }
            // . e.g. - Apr 22, Apr 23, etc
            searchCriterias.push({ property: 'Amount', operator: 'ne', comparison: '0' });

            return props.getSelectedItem(searchCriterias, false, 'Time Recorded', DrillDownType.timesheetSummary, 'Timesheet Lines');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<ReceiptOutlinedIcon />} title="Fees Recorded" />

                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.months[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            // var d = new Date();
                            // var year = d.getFullYear();
                            if (state.last === LastCountEnum.MONTH) {
                                searchCriterias.push(
                                    { property: 'DateShortString', operator: 'eq', comparison: item }, //
                                );
                            } else if (state.last === LastCountEnum.PREV_MONTH) {
                                searchCriterias.push(
                                    { property: 'DateShortString', operator: 'eq', comparison: item }, //
                                );
                            } else {
                                searchCriterias.push({ property: 'MonthStart', operator: 'gteq', comparison: item }, { property: 'MonthEnd', operator: 'lteq', comparison: item });
                            }
                            // . e.g. - Apr 22, Apr 23, etc
                            searchCriterias.push({ property: 'Amount', operator: 'ne', comparison: '0' });

                            return props.getSelectedItem(searchCriterias, false, 'Time Recorded - ' + item, DrillDownType.timesheetSummary, 'Timesheet Lines');
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Fees Recorded',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                            // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(state.counts),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.counts),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line:no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={LastCountEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={LastCountEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={LastCountEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;
export interface TimeFeesData {
    statistics: Statistics;
}

export interface Statistics {
    time: Time;
}

export interface Time {
    feesRecordedYearToDate: FeesRecordedYearToDate;
}

export interface FeesRecordedYearToDate {
    lastMonth: LastFinancialYear;
    lastFinancialYear: LastFinancialYear;
    previousFinancialYear: LastFinancialYear;
    previousMonth: PreviousMonth;
}

export interface LastFinancialYear {
    summary: Summary;
    timesheetGrouping: TimesheetGrouping[];
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
}

export interface TimesheetGrouping {
    value: number;
    group: string;
}

export interface PreviousMonth {
    summary: Summary;
    financialYearToPreviousMonthSummary: Summary;
    timesheetGrouping: TimesheetGrouping[];
}

const ChartData = gql`
    query FeesRecordedYearToDateChartData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            time {
                feesRecordedYearToDate(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }
                    lastFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }
                    previousFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }
                    previousMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        financialYearToPreviousMonthSummary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }
                }
            }
        }
    }
`;
